/**
 * File export utilities for downloading data as files.
 */

export interface DownloadFileOptions {
  /** The content to download */
  content: string | Blob;
  /** The filename (including extension) */
  filename: string;
  /** MIME type (e.g., 'text/csv', 'application/json') */
  mimeType: string;
}

/**
 * Triggers a file download in the browser.
 */
export function downloadFile({ content, filename, mimeType }: DownloadFileOptions): void {
  const blob = content instanceof Blob ? content : new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    document.body.removeChild(link);
  }, 100);
}

/**
 * Escapes a value for CSV (handles quotes and commas).
 */
export function escapeCSVValue(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }
  const stringValue = String(value);
  // If the value contains quotes, commas, or newlines, wrap in quotes and escape internal quotes
  if (stringValue.includes('"') || stringValue.includes(",") || stringValue.includes("\n")) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return `"${stringValue}"`;
}

export interface CSVExportOptions<T> {
  /** The data rows to export */
  data: T[];
  /** Column definitions: key is the object property, value is the header label */
  columns: { key: keyof T; header: string }[];
  /** Base filename (without extension, date will be appended) */
  filenameBase: string;
}

/**
 * Exports data as a CSV file with proper escaping.
 */
export function exportToCSV<T extends Record<string, unknown>>({
  data,
  columns,
  filenameBase,
}: CSVExportOptions<T>): void {
  const header = columns.map((col) => escapeCSVValue(col.header)).join(",");

  const rows = data.map((row) => columns.map((col) => escapeCSVValue(row[col.key])).join(","));

  const csvContent = [header, ...rows].join("\n");
  const dateStr = new Date().toISOString().split("T")[0];

  downloadFile({
    content: csvContent,
    filename: `${filenameBase}-${dateStr}.csv`,
    mimeType: "text/csv",
  });
}

/**
 * Exports data as a JSON file.
 */
export function exportToJSON<T>(data: T, filenameBase: string): void {
  const dateStr = new Date().toISOString().split("T")[0];

  downloadFile({
    content: JSON.stringify(data, null, 2),
    filename: `${filenameBase}-${dateStr}.json`,
    mimeType: "application/json",
  });
}
